/**
 * NPC Controller - Manages NPC movement, rotation, and animation
 * 
 * Features:
 * - State machine for movement (idle, walk, run, jump, etc.)
 * - Automatic animation playback based on state
 * - Smooth rotation toward movement direction
 * - Target-based movement (moveTo)
 * - Patrol system
 * - Animation speed synced to movement speed
 * - Event system for state changes
 */

import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { Scalar } from "@babylonjs/core/Maths/math.scalar";
import { Scene } from "@babylonjs/core/scene";

import { Entity } from "../entity/Entity";
import { AnimationController } from "../animation/AnimationController";
import { IDisposable, IUpdatable } from "../core/Engine.types";
import {
    NPCMovementState,
    NPCActionState,
    NPCEmotionState,
    NPCAnimationMap,
    NPCLocomotionConfig,
    NPCMovementInput,
    NPCMoveToTarget,
    NPCPatrolPoint,
    NPCControllerConfig,
    NPCStateSnapshot,
    NPCEventType,
    NPCEvent,
    NPCEventHandler,
} from "./NPC.types";

/**
 * Default locomotion settings
 */
const DEFAULT_LOCOMOTION: NPCLocomotionConfig = {
    walkSpeed: 2.0,
    runSpeed: 5.0,
    sprintSpeed: 8.0,
    rotationSpeed: 8.0,
    acceleration: 10.0,
    deceleration: 25.0, // Increased for snappier stops
    smoothRotation: true,
    walkThreshold: 0.3, // Increased - NPC goes to idle sooner
    runThreshold: 3.5,
    gravity: 20.0,
    jumpForce: 8.0,
};

/**
 * NPCController - Controls NPC movement, rotation, and animation
 */
export class NPCController implements IDisposable, IUpdatable {
    private _entity: Entity;
    private _scene: Scene;
    private _animController: AnimationController | null = null;
    private _config: NPCControllerConfig;
    private _locomotion: NPCLocomotionConfig;

    // State
    private _movementState: NPCMovementState = NPCMovementState.IDLE;
    private _actionState: NPCActionState = NPCActionState.NONE;
    private _emotionState: NPCEmotionState = NPCEmotionState.NEUTRAL;
    
    // Movement
    private _velocity: Vector3 = Vector3.Zero();
    private _targetVelocity: Vector3 = Vector3.Zero();
    private _currentSpeed: number = 0;
    private _isGrounded: boolean = true;
    private _verticalVelocity: number = 0;
    
    // Obstacle avoidance
    private _obstacleAvoidanceEnabled: boolean = true;
    private _obstacleCheckRadius: number = 1.2; // NPC collision radius (increased for tree trunks)
    private _obstacleCheckDistance: number = 3.5; // How far ahead to check (increased for early detection)
    private _obstacleRayHeights: number[] = [0.5, 1.0, 1.8]; // Multiple ray heights for better detection
    
    // Rotation
    private _targetRotationY: number = 0;
    private _currentRotationY: number = 0;
    
    // Move-to target
    private _moveTarget: NPCMoveToTarget | null = null;
    
    // Patrol
    private _patrolPoints: NPCPatrolPoint[] = [];
    private _currentPatrolIndex: number = 0;
    private _patrolWaitTimer: number = 0;
    private _isPatrolling: boolean = false;
    private _patrolLoop: boolean = true;
    
    // Animation
    private _currentAnimName: string | null = null;
    private _blendTime: number = 0.2;
    private _syncAnimSpeed: boolean = true;
    
    // Terrain/ground height callback
    private _getGroundHeight: ((x: number, z: number) => number) | null = null;
    
    // Events
    private _eventHandlers: Map<NPCEventType, Set<NPCEventHandler>> = new Map();

    /**
     * Get current movement state
     */
    public get movementState(): NPCMovementState {
        return this._movementState;
    }

    /**
     * Get current action state
     */
    public get actionState(): NPCActionState {
        return this._actionState;
    }

    /**
     * Get current emotion state
     */
    public get emotionState(): NPCEmotionState {
        return this._emotionState;
    }

    /**
     * Get current velocity
     */
    public get velocity(): Vector3 {
        return this._velocity.clone();
    }

    /**
     * Get current speed (magnitude of horizontal velocity)
     */
    public get currentSpeed(): number {
        return this._currentSpeed;
    }

    /**
     * Check if NPC is grounded
     */
    public get isGrounded(): boolean {
        return this._isGrounded;
    }

    /**
     * Check if NPC is moving
     */
    public get isMoving(): boolean {
        return this._currentSpeed > this._locomotion.walkThreshold;
    }

    /**
     * Check if NPC has a move target
     */
    public get hasMoveTarget(): boolean {
        return this._moveTarget !== null;
    }

    /**
     * Get the animation controller
     */
    public get animationController(): AnimationController | null {
        return this._animController;
    }

    constructor(entity: Entity, scene: Scene, config: Partial<NPCControllerConfig> = {}) {
        this._entity = entity;
        this._scene = scene;
        
        // Merge config with defaults
        this._config = {
            animations: config.animations || {},
            locomotion: config.locomotion || {},
            initialState: config.initialState || NPCMovementState.IDLE,
            useRootMotion: config.useRootMotion ?? false,
            syncAnimationSpeed: config.syncAnimationSpeed ?? true,
            blendTime: config.blendTime ?? 0.2,
            getGroundHeight: config.getGroundHeight,
        };

        this._locomotion = { ...DEFAULT_LOCOMOTION, ...this._config.locomotion };
        this._blendTime = this._config.blendTime ?? 0.2;
        this._syncAnimSpeed = this._config.syncAnimationSpeed ?? true;
        this._getGroundHeight = config.getGroundHeight ?? null;

        // Initialize animation controller if entity has animations
        this._initializeAnimations();

        // Get initial rotation
        const euler = this._entity.rotation.toEulerAngles();
        this._currentRotationY = euler.y;
        this._targetRotationY = euler.y;

        // Set initial state
        this._setMovementState(this._config.initialState || NPCMovementState.IDLE);
    }

    /**
     * Initialize animation controller from entity's animation groups
     */
    private _initializeAnimations(): void {
        const animGroups = (this._entity as any)._animationGroups;
        const skeleton = this._entity.skeleton;

        if (animGroups && animGroups.length > 0) {
            this._animController = new AnimationController(animGroups, skeleton);
            
            // Log available animations for mapping
            console.log(`NPCController: Available animations for "${this._entity.name}":`, 
                this._animController.animationNames);
            
            // Auto-detect common animation names if not mapped
            this._autoDetectAnimations();
        } else {
            console.warn(`NPCController: No animations found for entity "${this._entity.name}"`);
        }
    }

    /**
     * Auto-detect common animation names from the model
     */
    private _autoDetectAnimations(): void {
        if (!this._animController) return;

        const names = this._animController.animationNames;
        const animations = this._config.animations;

        // Helper to find animation by keywords
        const findAnim = (keywords: string[]): string | undefined => {
            for (const keyword of keywords) {
                const found = names.find(n => 
                    n.toLowerCase().includes(keyword.toLowerCase())
                );
                if (found) return found;
            }
            return undefined;
        };

        // Auto-detect if not already mapped
        if (!animations.idle) {
            animations.idle = findAnim(["idle", "stand", "rest"]);
        }
        if (!animations.walk) {
            animations.walk = findAnim(["walk", "locomotion"]);
        }
        if (!animations.run) {
            animations.run = findAnim(["run", "jog", "sprint"]);
        }
        if (!animations.jump) {
            animations.jump = findAnim(["jump", "leap"]);
        }
        if (!animations.attack) {
            animations.attack = findAnim(["attack", "hit", "punch", "kick", "strike", "swipe"]);
        }
        if (!animations.die) {
            animations.die = findAnim(["death", "die", "dying"]);
        }
        if (!animations.eat) {
            animations.eat = findAnim(["eat", "eating", "feed"]);
        }

        console.log(`NPCController: Auto-detected animations:`, animations);
    }

    /**
     * Set animation mapping for a state
     */
    public setAnimation(key: keyof NPCAnimationMap, animationName: string | string[]): void {
        this._config.animations[key] = animationName;
    }

    /**
     * Set the ground height callback for terrain following
     * @param callback Function that returns ground height at (x, z) position
     */
    public setGroundHeightCallback(callback: (x: number, z: number) => number): void {
        this._getGroundHeight = callback;
    }

    /**
     * Set locomotion parameter
     */
    public setLocomotion<K extends keyof NPCLocomotionConfig>(
        key: K, 
        value: NPCLocomotionConfig[K]
    ): void {
        this._locomotion[key] = value;
    }

    /**
     * Process movement input (for player-controlled or AI input)
     */
    public processInput(input: Partial<NPCMovementInput>): void {
        // Clear any move-to target when manual input received
        if (input.forward !== undefined || input.right !== undefined) {
            this._moveTarget = null;
        }

        const forward = input.forward ?? 0;
        const right = input.right ?? 0;
        const run = input.run ?? false;
        const sprint = input.sprint ?? false;

        // Calculate target speed
        let targetSpeed = 0;
        if (Math.abs(forward) > 0.01 || Math.abs(right) > 0.01) {
            if (sprint) {
                targetSpeed = this._locomotion.sprintSpeed;
            } else if (run) {
                targetSpeed = this._locomotion.runSpeed;
            } else {
                targetSpeed = this._locomotion.walkSpeed;
            }
        }

        // Calculate movement direction in world space
        // Get entity's forward and right vectors
        const entityForward = this._entity.forward.normalize();
        const entityRight = Vector3.Cross(Vector3.Up(), entityForward).normalize();

        // Calculate target velocity
        const moveDir = entityForward.scale(forward).add(entityRight.scale(right));
        if (moveDir.length() > 0.01) {
            moveDir.normalize();
            this._targetVelocity = moveDir.scale(targetSpeed);
            
            // Calculate target rotation to face movement direction
            this._targetRotationY = Math.atan2(moveDir.x, moveDir.z);
        } else {
            this._targetVelocity = Vector3.Zero();
        }

        // Handle jump
        if (input.jump && this._isGrounded) {
            this._verticalVelocity = this._locomotion.jumpForce;
            this._isGrounded = false;
            this._setMovementState(NPCMovementState.JUMPING);
        }

        // Handle crouch
        if (input.crouch) {
            if (this._movementState !== NPCMovementState.CROUCHING) {
                this._setMovementState(NPCMovementState.CROUCHING);
            }
        } else if (this._movementState === NPCMovementState.CROUCHING) {
            this._setMovementState(NPCMovementState.IDLE);
        }
    }

    /**
     * Move toward a target position
     */
    public moveTo(target: Vector3, options?: Partial<Omit<NPCMoveToTarget, "position">>): void {
        this._moveTarget = {
            position: target.clone(),
            stopDistance: options?.stopDistance ?? 0.5,
            run: options?.run ?? false,
            onReached: options?.onReached,
            onBlocked: options?.onBlocked,
        };
    }

    /**
     * Stop moving to target
     */
    public stopMoving(): void {
        this._moveTarget = null;
        this._targetVelocity = Vector3.Zero();
        this._isPatrolling = false;
    }

    /**
     * Look at a target position (rotate to face)
     */
    public lookAt(target: Vector3): void {
        const direction = target.subtract(this._entity.position);
        direction.y = 0; // Ignore vertical
        
        if (direction.length() > 0.01) {
            this._targetRotationY = Math.atan2(direction.x, direction.z);
        }
    }

    /**
     * Set rotation directly (radians)
     */
    public setRotation(angleY: number, instant: boolean = false): void {
        this._targetRotationY = angleY;
        if (instant) {
            this._currentRotationY = angleY;
            this._applyRotation();
        }
    }

    /**
     * Get current rotation in degrees (0 = North, 90 = East, etc.)
     */
    public getRotationDegrees(): number {
        let degrees = (this._currentRotationY * 180 / Math.PI) % 360;
        if (degrees < 0) degrees += 360;
        return degrees;
    }

    /**
     * Get cardinal direction the NPC is facing
     */
    public getFacingDirection(): string {
        const deg = this.getRotationDegrees();
        if (deg >= 337.5 || deg < 22.5) return "North";
        if (deg >= 22.5 && deg < 67.5) return "Northeast";
        if (deg >= 67.5 && deg < 112.5) return "East";
        if (deg >= 112.5 && deg < 157.5) return "Southeast";
        if (deg >= 157.5 && deg < 202.5) return "South";
        if (deg >= 202.5 && deg < 247.5) return "Southwest";
        if (deg >= 247.5 && deg < 292.5) return "West";
        return "Northwest";
    }

    /**
     * Start patrolling between points
     */
    public patrol(points: NPCPatrolPoint[], loop: boolean = true): void {
        if (points.length === 0) return;
        
        this._patrolPoints = points;
        this._currentPatrolIndex = 0;
        this._patrolLoop = loop;
        this._isPatrolling = true;
        this._patrolWaitTimer = 0;

        console.log(`NPCController: Starting patrol with ${points.length} points`);
        points.forEach((p, i) => {
            console.log(`  Point ${i}: (${p.position.x.toFixed(1)}, ${p.position.y.toFixed(1)}, ${p.position.z.toFixed(1)}), run=${p.run}`);
        });

        // Move to first point
        this._moveToPatrolPoint(0);
    }

    /**
     * Stop patrolling
     */
    public stopPatrol(): void {
        this._isPatrolling = false;
        this._patrolPoints = [];
        this.stopMoving();
    }

    /**
     * Perform an action (plays animation, sets action state)
     */
    public performAction(
        action: NPCActionState, 
        options?: { 
            animationOverride?: string;
            loop?: boolean;
            onComplete?: () => void;
        }
    ): void {
        this._setActionState(action);

        // Get animation name for this action
        let animName: string | undefined = options?.animationOverride;
        if (!animName) {
            const mapping = this._getActionAnimationKey(action);
            animName = this._resolveAnimationName(mapping) ?? undefined;
        }

        if (animName && this._animController) {
            const loop = options?.loop ?? false;
            this._animController.play(animName, {
                loop,
                blendTime: this._blendTime,
                onEnd: () => {
                    if (!loop) {
                        this._setActionState(NPCActionState.NONE);
                        options?.onComplete?.();
                    }
                },
            });
        }
    }

    /**
     * Set emotion state
     */
    public setEmotion(emotion: NPCEmotionState): void {
        if (this._emotionState === emotion) return;

        const prev = this._emotionState;
        this._emotionState = emotion;
        
        this._emit("emotionChange", { previousState: prev, currentState: emotion });

        // Play emotion animation if mapped
        const emoteKey = `emote${emotion.charAt(0).toUpperCase() + emotion.slice(1)}` as keyof NPCAnimationMap;
        const animName = this._resolveAnimationName(emoteKey);
        if (animName && this._animController) {
            this._animController.play(animName, { loop: false, blendTime: 0.3 });
        }
    }

    /**
     * Play a custom animation by name
     */
    public playAnimation(
        name: string, 
        options?: { 
            loop?: boolean; 
            speed?: number; 
            blendTime?: number;
            onEnd?: () => void;
        }
    ): boolean {
        if (!this._animController) return false;
        
        return this._animController.play(name, {
            loop: options?.loop ?? true,
            speed: options?.speed ?? 1.0,
            blendTime: options?.blendTime ?? this._blendTime,
            onEnd: options?.onEnd,
        });
    }

    /**
     * Stop current animation
     */
    public stopAnimation(): void {
        this._animController?.stop();
    }

    /**
     * Update NPC (call every frame)
     */
    public update(deltaTime: number): void {
        // Debug: Log state occasionally
        if (Math.random() < 0.01) { // ~1% of frames
            console.log(`NPC Update: state=${this._movementState}, speed=${this._currentSpeed.toFixed(2)}, patrolling=${this._isPatrolling}, target=${this._moveTarget !== null}, waitTimer=${this._patrolWaitTimer.toFixed(2)}`);
        }
        
        // Update move-to target logic
        this._updateMoveToTarget(deltaTime);
        
        // Update patrol logic
        this._updatePatrol(deltaTime);
        
        // Update velocity with acceleration/deceleration
        this._updateVelocity(deltaTime);
        
        // Update rotation
        this._updateRotation(deltaTime);
        
        // Apply movement
        this._applyMovement(deltaTime);
        
        // Update movement state based on velocity
        this._updateMovementState();
        
        // Update animation based on state
        this._updateAnimation();
    }

    /**
     * Update move-to target logic
     */
    private _updateMoveToTarget(_deltaTime: number): void {
        if (!this._moveTarget) return;

        const target = this._moveTarget.position;
        const pos = this._entity.position;
        
        // Calculate horizontal distance
        const dx = target.x - pos.x;
        const dz = target.z - pos.z;
        const distance = Math.sqrt(dx * dx + dz * dz);

        // Debug occasionally
        if (Math.random() < 0.02) {
            console.log(`NPCController: Moving to target, distance=${distance.toFixed(2)}, stopDist=${this._moveTarget.stopDistance}`);
        }

        // Check if reached
        if (distance <= this._moveTarget.stopDistance) {
            console.log(`NPCController: Reached target at distance ${distance.toFixed(2)}`);
            const callback = this._moveTarget.onReached;
            this._moveTarget = null;
            this._targetVelocity = Vector3.Zero();
            this._emit("reachedTarget");
            callback?.();
            return;
        }

        // Calculate direction and set velocity
        const direction = new Vector3(dx / distance, 0, dz / distance);
        const speed = this._moveTarget.run 
            ? this._locomotion.runSpeed 
            : this._locomotion.walkSpeed;
        
        this._targetVelocity = direction.scale(speed);
        
        // Face movement direction
        this._targetRotationY = Math.atan2(dx, dz);
    }

    /**
     * Update patrol logic
     */
    private _updatePatrol(deltaTime: number): void {
        if (!this._isPatrolling || this._patrolPoints.length === 0) return;

        // If currently waiting at a point
        if (this._patrolWaitTimer > 0) {
            this._patrolWaitTimer -= deltaTime;
            if (this._patrolWaitTimer <= 0) {
                // Move to next patrol point
                this._advancePatrol();
            }
            return;
        }

        // If not moving to target, we've reached the patrol point
        if (!this._moveTarget) {
            const currentPoint = this._patrolPoints[this._currentPatrolIndex];
            
            // Play wait animation if specified
            if (currentPoint.waitAnimation && this._animController) {
                this._animController.play(currentPoint.waitAnimation, { loop: true });
            }
            
            // Fire callback
            currentPoint.onReached?.();
            
            // Start wait timer
            this._patrolWaitTimer = currentPoint.waitTime;
        }
    }

    /**
     * Advance to next patrol point
     */
    private _advancePatrol(): void {
        this._currentPatrolIndex++;
        
        if (this._currentPatrolIndex >= this._patrolPoints.length) {
            if (this._patrolLoop) {
                this._currentPatrolIndex = 0;
            } else {
                this._isPatrolling = false;
                return;
            }
        }

        this._moveToPatrolPoint(this._currentPatrolIndex);
    }

    /**
     * Move to a patrol point by index
     */
    private _moveToPatrolPoint(index: number): void {
        const point = this._patrolPoints[index];
        console.log(`NPCController: Moving to patrol point ${index} at (${point.position.x.toFixed(1)}, ${point.position.y.toFixed(1)}, ${point.position.z.toFixed(1)}), run=${point.run}`);
        this.moveTo(point.position, { stopDistance: 0.5, run: point.run ?? false });
    }

    /**
     * Update velocity with acceleration
     */
    private _updateVelocity(deltaTime: number): void {
        // Horizontal velocity
        const targetHoriz = new Vector3(this._targetVelocity.x, 0, this._targetVelocity.z);
        const currentHoriz = new Vector3(this._velocity.x, 0, this._velocity.z);
        
        // Debug: Log target velocity if non-zero
        if (targetHoriz.length() > 0.01) {
            console.log(`NPCController: _updateVelocity - targetVel=(${this._targetVelocity.x.toFixed(2)}, ${this._targetVelocity.z.toFixed(2)}), currentVel=(${this._velocity.x.toFixed(2)}, ${this._velocity.z.toFixed(2)})`);
        }
        
        const diff = targetHoriz.subtract(currentHoriz);
        const accel = diff.length() > 0.01 
            ? (targetHoriz.length() > currentHoriz.length() 
                ? this._locomotion.acceleration 
                : this._locomotion.deceleration)
            : this._locomotion.deceleration;

        // Apply acceleration
        if (diff.length() > accel * deltaTime) {
            const accelDir = diff.normalize().scale(accel * deltaTime);
            this._velocity.x += accelDir.x;
            this._velocity.z += accelDir.z;
        } else {
            this._velocity.x = targetHoriz.x;
            this._velocity.z = targetHoriz.z;
        }

        // Vertical velocity (gravity)
        if (!this._isGrounded) {
            this._verticalVelocity -= this._locomotion.gravity * deltaTime;
        }
        this._velocity.y = this._verticalVelocity;

        // Calculate current speed
        this._currentSpeed = Math.sqrt(
            this._velocity.x * this._velocity.x + 
            this._velocity.z * this._velocity.z
        );
    }

    /**
     * Update rotation smoothly toward target
     */
    private _updateRotation(deltaTime: number): void {
        if (!this._locomotion.smoothRotation) {
            this._currentRotationY = this._targetRotationY;
            this._applyRotation();
            return;
        }

        // Smooth rotation with wrapping
        let diff = this._targetRotationY - this._currentRotationY;
        
        // Wrap to -PI to PI
        while (diff > Math.PI) diff -= Math.PI * 2;
        while (diff < -Math.PI) diff += Math.PI * 2;

        // Apply rotation speed
        const maxRotation = this._locomotion.rotationSpeed * deltaTime;
        if (Math.abs(diff) > maxRotation) {
            this._currentRotationY += Math.sign(diff) * maxRotation;
        } else {
            this._currentRotationY = this._targetRotationY;
        }

        // Wrap current rotation
        while (this._currentRotationY > Math.PI) this._currentRotationY -= Math.PI * 2;
        while (this._currentRotationY < -Math.PI) this._currentRotationY += Math.PI * 2;

        this._applyRotation();
    }

    /**
     * Apply current rotation to entity
     */
    private _applyRotation(): void {
        this._entity.rotation = Quaternion.FromEulerAngles(0, this._currentRotationY, 0);
    }

    /**
     * Apply movement to entity position with obstacle avoidance
     */
    private _applyMovement(deltaTime: number): void {
        const oldPos = this._entity.position.clone();
        
        if (!this._config.useRootMotion) {
            // Calculate intended movement
            let movement = this._velocity.scale(deltaTime);
            
            // Apply obstacle avoidance if enabled and moving
            if (this._obstacleAvoidanceEnabled && movement.length() > 0.001) {
                movement = this._avoidObstacles(movement);
            }
            
            // Apply the (possibly adjusted) movement
            this._entity.position.addInPlace(movement);
            
            // Debug: Log if we moved
            if (movement.length() > 0.001) {
                console.log(`NPCController: Applied movement (${movement.x.toFixed(3)}, ${movement.y.toFixed(3)}, ${movement.z.toFixed(3)}), velocity=(${this._velocity.x.toFixed(2)}, ${this._velocity.z.toFixed(2)})`);
            }
        }

        // Get ground height at current position
        const pos = this._entity.position;
        const groundHeight = this._getGroundHeight 
            ? this._getGroundHeight(pos.x, pos.z) 
            : 0;

        // Terrain following: Always keep entity on terrain when grounded
        // This handles both falling below terrain AND walking uphill/downhill
        const heightDiff = pos.y - groundHeight;
        const snapThreshold = 1.0; // Max height above terrain to snap down

        if (heightDiff <= 0) {
            // Below or at terrain - snap to terrain
            this._entity.position.y = groundHeight;
            this._verticalVelocity = 0;
            
            if (!this._isGrounded) {
                this._isGrounded = true;
                if (this._movementState === NPCMovementState.JUMPING || 
                    this._movementState === NPCMovementState.FALLING) {
                    this._setMovementState(NPCMovementState.LANDING);
                }
            }
        } else if (this._isGrounded && heightDiff < snapThreshold) {
            // Slightly above terrain but grounded - snap down (walking downhill)
            this._entity.position.y = groundHeight;
            this._verticalVelocity = 0;
        } else if (heightDiff >= snapThreshold) {
            // Too high above terrain - start falling
            this._isGrounded = false;
        }
        
        // IMPORTANT: Sync physics body transform after moving
        // This is needed for kinematic physics bodies
        if (this._entity.hasPhysics) {
            this._entity.syncPhysics();
        }
        
        // Debug: Log position change
        const newPos = this._entity.position;
        const moved = newPos.subtract(oldPos).length();
        if (moved > 0.01) {
            console.log(`NPCController: Position changed from (${oldPos.x.toFixed(2)}, ${oldPos.y.toFixed(2)}, ${oldPos.z.toFixed(2)}) to (${newPos.x.toFixed(2)}, ${newPos.y.toFixed(2)}, ${newPos.z.toFixed(2)})`);
        }
    }
    
    // Debug counter for periodic logging
    private _debugRaycastCounter: number = 0;
    
    /**
     * Check for obstacles in the movement direction and adjust movement
     * Uses physics raycasting to detect collisions with static objects
     * Casts multiple rays at different heights for more reliable detection
     */
    private _avoidObstacles(movement: Vector3): Vector3 {
        const pos = this._entity.position;
        const physicsEngine = this._scene.getPhysicsEngine();
        
        if (!physicsEngine) {
            console.warn("NPCController: No physics engine available for obstacle detection");
            return movement; // No physics engine, can't raycast
        }
        
        const movementDir = movement.normalizeToNew();
        const rayLength = this._obstacleCheckDistance + movement.length();
        
        // Cast multiple rays at different heights for better obstacle detection
        let closestHitDistance = Infinity;
        let hasHit = false;
        let hitBodyName = "";
        
        // Periodic debug logging (every 60 frames)
        this._debugRaycastCounter++;
        const shouldLog = this._debugRaycastCounter % 60 === 0;
        
        // Also cast a ray directly from center for maximum reliability
        const centerRayOrigin = pos.add(new Vector3(0, 1.0, 0));
        const centerRayEnd = centerRayOrigin.add(movementDir.scale(rayLength));
        const centerResult = physicsEngine.raycast(centerRayOrigin, centerRayEnd);
        
        if (centerResult.hasHit && centerResult.hitDistance < closestHitDistance) {
            closestHitDistance = centerResult.hitDistance;
            hasHit = true;
            hitBodyName = centerResult.body?.transformNode?.name ?? "unknown";
        }
        
        for (const rayHeight of this._obstacleRayHeights) {
            // Raycast from NPC at this height in movement direction
            const rayOrigin = pos.add(new Vector3(0, rayHeight, 0));
            const rayEnd = rayOrigin.add(movementDir.scale(rayLength));
            const result = physicsEngine.raycast(rayOrigin, rayEnd);
            
            if (shouldLog && rayHeight === this._obstacleRayHeights[0]) {
                console.log(`NPCController raycast: from (${rayOrigin.x.toFixed(1)}, ${rayOrigin.y.toFixed(1)}, ${rayOrigin.z.toFixed(1)}) -> dir (${movementDir.x.toFixed(2)}, ${movementDir.z.toFixed(2)}), len=${rayLength.toFixed(1)}, hit=${result.hasHit}, dist=${result.hitDistance?.toFixed(2) ?? 'N/A'}`);
            }
            
            if (result.hasHit && result.hitDistance < closestHitDistance) {
                closestHitDistance = result.hitDistance;
                hasHit = true;
                hitBodyName = result.body?.transformNode?.name ?? "unknown";
            }
        }
        
        if (hasHit) {
            // HARD STOP: If obstacle is within collision radius, stop completely
            if (closestHitDistance < this._obstacleCheckRadius) {
                console.log(`NPCController: HARD STOP - Obstacle "${hitBodyName}" too close at ${closestHitDistance.toFixed(2)}m (radius=${this._obstacleCheckRadius})`);
                return Vector3.Zero();
            }
            
            // If obstacle is closer than our collision radius + movement distance
            if (closestHitDistance < this._obstacleCheckRadius + movement.length()) {
                // Calculate how much we can move before hitting
                const safeDistance = Math.max(0, closestHitDistance - this._obstacleCheckRadius - 0.2);
                
                if (safeDistance < movement.length() * 0.2) {
                    // Too close to obstacle - stop completely
                    console.log(`NPCController: Obstacle "${hitBodyName}" detected at ${closestHitDistance.toFixed(2)}m - stopping`);
                    return Vector3.Zero();
                } else {
                    // Reduce movement to safe distance
                    console.log(`NPCController: Obstacle "${hitBodyName}" at ${closestHitDistance.toFixed(2)}m - reducing movement to ${safeDistance.toFixed(2)}`);
                    return movementDir.scale(safeDistance);
                }
            }
        }
        
        // Also check diagonally for wider obstacles
        const leftDir = new Vector3(
            movementDir.x * Math.cos(0.5) - movementDir.z * Math.sin(0.5),
            0,
            movementDir.x * Math.sin(0.5) + movementDir.z * Math.cos(0.5)
        );
        const rightDir = new Vector3(
            movementDir.x * Math.cos(-0.5) - movementDir.z * Math.sin(-0.5),
            0,
            movementDir.x * Math.sin(-0.5) + movementDir.z * Math.cos(-0.5)
        );
        
        // Check left side with multiple heights
        for (const rayHeight of this._obstacleRayHeights) {
            const leftOrigin = pos.add(new Vector3(0, rayHeight, 0)).add(leftDir.scale(this._obstacleCheckRadius));
            const leftEnd = leftOrigin.add(movementDir.scale(rayLength * 0.7));
            const leftResult = physicsEngine.raycast(leftOrigin, leftEnd);
            if (leftResult.hasHit && leftResult.hitDistance < this._obstacleCheckRadius + movement.length()) {
                // Obstacle on left - try to steer right
                const steerAmount = 0.3;
                return movement.add(rightDir.scale(movement.length() * steerAmount)).normalize().scale(movement.length() * 0.8);
            }
        }
        
        // Check right side with multiple heights
        for (const rayHeight of this._obstacleRayHeights) {
            const rightOrigin = pos.add(new Vector3(0, rayHeight, 0)).add(rightDir.scale(this._obstacleCheckRadius));
            const rightEnd = rightOrigin.add(movementDir.scale(rayLength * 0.7));
            const rightResult = physicsEngine.raycast(rightOrigin, rightEnd);
            if (rightResult.hasHit && rightResult.hitDistance < this._obstacleCheckRadius + movement.length()) {
                // Obstacle on right - try to steer left
                const steerAmount = 0.3;
                return movement.add(leftDir.scale(movement.length() * steerAmount)).normalize().scale(movement.length() * 0.8);
            }
        }
        
        return movement;
    }
    
    /**
     * Enable or disable obstacle avoidance
     * @param enabled - Whether obstacle avoidance is enabled
     * @param radius - NPC collision radius for obstacle detection
     * @param distance - How far ahead to check for obstacles
     * @param rayHeights - Array of heights (from ground) to cast rays at
     */
    public setObstacleAvoidance(
        enabled: boolean, 
        radius?: number, 
        distance?: number,
        rayHeights?: number[]
    ): void {
        this._obstacleAvoidanceEnabled = enabled;
        if (radius !== undefined) this._obstacleCheckRadius = radius;
        if (distance !== undefined) this._obstacleCheckDistance = distance;
        if (rayHeights !== undefined) this._obstacleRayHeights = rayHeights;
    }

    /**
     * Update movement state based on velocity
     */
    private _updateMovementState(): void {
        // Don't override action states
        if (this._actionState !== NPCActionState.NONE) return;
        
        // Handle landing recovery - transition to appropriate ground state
        if (this._movementState === NPCMovementState.LANDING) {
            // Landing is a brief transitional state - immediately check ground movement
            // In a full implementation, you might wait for landing animation to complete
            if (this._isGrounded) {
                // Transition based on current velocity
                if (this._currentSpeed >= this._locomotion.walkThreshold) {
                    this._setMovementState(this._currentSpeed >= this._locomotion.runThreshold 
                        ? NPCMovementState.RUNNING 
                        : NPCMovementState.WALKING);
                } else {
                    this._setMovementState(NPCMovementState.IDLE);
                }
            }
            return;
        }

        // Handle jump/fall
        if (!this._isGrounded) {
            if (this._verticalVelocity < -0.1) {
                this._setMovementState(NPCMovementState.FALLING);
            }
            return;
        }

        // Ground movement states
        if (this._currentSpeed < this._locomotion.walkThreshold) {
            this._setMovementState(NPCMovementState.IDLE);
        } else if (this._currentSpeed >= this._locomotion.runThreshold) {
            this._setMovementState(NPCMovementState.RUNNING);
        } else {
            this._setMovementState(NPCMovementState.WALKING);
        }
    }

    /**
     * Set movement state with event emission
     */
    private _setMovementState(state: NPCMovementState): void {
        if (this._movementState === state) return;

        const prev = this._movementState;
        this._movementState = state;
        
        this._emit("stateChange", { previousState: prev, currentState: state });
    }

    /**
     * Set action state with event emission
     */
    private _setActionState(state: NPCActionState): void {
        if (this._actionState === state) return;

        const prev = this._actionState;
        this._actionState = state;
        
        if (state !== NPCActionState.NONE) {
            this._emit("actionStart", { currentState: state });
        } else {
            this._emit("actionEnd", { previousState: prev });
        }
    }

    /**
     * Update animation based on current state
     */
    private _updateAnimation(): void {
        if (!this._animController) return;
        if (this._actionState !== NPCActionState.NONE) return; // Action takes priority

        // Get animation for current movement state
        const animKey = this._getMovementAnimationKey(this._movementState);
        const animName = this._resolveAnimationName(animKey);
        
        // Debug log state changes
        if (animName !== this._currentAnimName) {
            console.log(`NPCController: Animation change - state=${this._movementState}, anim=${animName}, prevAnim=${this._currentAnimName}`);
        }
        
        if (!animName) {
            console.warn(`NPCController: No animation found for state ${this._movementState} (key: ${animKey})`);
            return;
        }
        
        // Calculate speed ratio for walk/run sync
        let speedRatio = 1.0;
        if (this._syncAnimSpeed && this._movementState === NPCMovementState.WALKING) {
            speedRatio = this._currentSpeed / this._locomotion.walkSpeed;
            speedRatio = Scalar.Clamp(speedRatio, 0.5, 1.5);
        } else if (this._syncAnimSpeed && this._movementState === NPCMovementState.RUNNING) {
            speedRatio = this._currentSpeed / this._locomotion.runSpeed;
            speedRatio = Scalar.Clamp(speedRatio, 0.5, 1.5);
        }
        // IDLE and other states always play at normal speed (1.0)
        
        // Only switch animation if it's different
        if (animName !== this._currentAnimName) {
            this._currentAnimName = animName;
            this._animController.play(animName, {
                loop: true,
                speed: speedRatio,
                blendTime: this._blendTime,
            });
        } else if (this._movementState === NPCMovementState.WALKING || 
                   this._movementState === NPCMovementState.RUNNING) {
            // Update speed ratio for movement animations without restarting
            this._animController.setSpeed(speedRatio);
        }
    }

    /**
     * Get animation key for movement state
     */
    private _getMovementAnimationKey(state: NPCMovementState): keyof NPCAnimationMap {
        switch (state) {
            case NPCMovementState.IDLE: return "idle";
            case NPCMovementState.WALKING: return "walk";
            case NPCMovementState.RUNNING: return "run";
            case NPCMovementState.SPRINTING: return "sprint";
            case NPCMovementState.JUMPING: return "jump";
            case NPCMovementState.FALLING: return "fall";
            case NPCMovementState.LANDING: return "land";
            case NPCMovementState.CROUCHING: return "crouch";
            case NPCMovementState.CRAWLING: return "crawl";
            case NPCMovementState.SWIMMING: return "swim";
            case NPCMovementState.CLIMBING: return "climb";
            case NPCMovementState.FLYING: return "fly";
            default: return "idle";
        }
    }

    /**
     * Get animation key for action state
     */
    private _getActionAnimationKey(state: NPCActionState): keyof NPCAnimationMap {
        switch (state) {
            case NPCActionState.ATTACKING: return "attack";
            case NPCActionState.BLOCKING: return "block";
            case NPCActionState.INTERACTING: return "interact";
            case NPCActionState.PICKING_UP: return "pickUp";
            case NPCActionState.THROWING: return "throw";
            case NPCActionState.EATING: return "eat";
            case NPCActionState.DRINKING: return "drink";
            case NPCActionState.SLEEPING: return "sleep";
            case NPCActionState.SITTING: return "sit";
            case NPCActionState.DYING: return "die";
            case NPCActionState.DEAD: return "dead";
            default: return "idle";
        }
    }

    /**
     * Resolve animation name from mapping (handles arrays)
     */
    private _resolveAnimationName(key: keyof NPCAnimationMap): string | null {
        const value = this._config.animations[key];
        if (!value) return null;
        
        if (Array.isArray(value)) {
            // Random from array
            return value[Math.floor(Math.random() * value.length)];
        }
        return value;
    }

    /**
     * Subscribe to NPC events
     */
    public on(event: NPCEventType, handler: NPCEventHandler): void {
        if (!this._eventHandlers.has(event)) {
            this._eventHandlers.set(event, new Set());
        }
        this._eventHandlers.get(event)!.add(handler);
    }

    /**
     * Unsubscribe from NPC events
     */
    public off(event: NPCEventType, handler: NPCEventHandler): void {
        this._eventHandlers.get(event)?.delete(handler);
    }

    /**
     * Emit NPC event
     */
    private _emit(type: NPCEventType, data?: Partial<Omit<NPCEvent, "type">>): void {
        const handlers = this._eventHandlers.get(type);
        if (!handlers) return;

        const event: NPCEvent = { type, ...data };
        handlers.forEach(h => h(event));
    }

    /**
     * Get state snapshot for debugging/serialization
     */
    public getStateSnapshot(): NPCStateSnapshot {
        return {
            movementState: this._movementState,
            actionState: this._actionState,
            emotionState: this._emotionState,
            position: {
                x: this._entity.position.x,
                y: this._entity.position.y,
                z: this._entity.position.z,
            },
            rotation: this._currentRotationY,
            velocity: {
                x: this._velocity.x,
                y: this._velocity.y,
                z: this._velocity.z,
            },
            currentAnimation: this._currentAnimName,
            isGrounded: this._isGrounded,
            moveTarget: this._moveTarget ? {
                x: this._moveTarget.position.x,
                y: this._moveTarget.position.y,
                z: this._moveTarget.position.z,
            } : null,
        };
    }

    /**
     * Get obstacle avoidance radius (for debug visualization)
     */
    public get obstacleCheckRadius(): number {
        return this._obstacleCheckRadius;
    }

    /**
     * Get obstacle check distance (for debug visualization)
     */
    public get obstacleCheckDistance(): number {
        return this._obstacleCheckDistance;
    }

    /**
     * Get ray heights used for obstacle detection (for debug visualization)
     */
    public get obstacleRayHeights(): number[] {
        return [...this._obstacleRayHeights];
    }

    /**
     * Dispose NPC controller
     */
    public dispose(): void {
        this.stopMoving();
        this.stopPatrol();
        this._eventHandlers.clear();
        // AnimationController is owned by entity, not disposed here
    }
}
